import { ArrowLeftIcon, ArrowRightIcon, CircleNotchIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  type AdminUserSummary,
  apiErrorStatus,
  useAddWorkspaceMember,
  useAdminUsers,
  useAdminWorkspace,
  useAssignStaff,
  useUnassignStaff,
  type WorkspaceEngagement,
  type WorkspacePerson,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { clientCandidates, personLabel, staffCandidates, workspaceErrorMessage } from "./utils";

type Message = { ok: boolean; text: string } | null;

/** One client workspace: its client users and, per engagement, the assigned staff. */
export function AdminWorkspacePage() {
  const { workspaceId = "" } = useParams();
  const workspaceQuery = useAdminWorkspace(workspaceId);
  const usersQuery = useAdminUsers();
  const workspace = workspaceQuery.data;
  const users = usersQuery.data ?? [];

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <h1 className="t-heading text-foreground">Workspaces</h1>
        <Link to="/cockpit">
          <Button variant="outline">Back</Button>
        </Link>
      </header>
      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        <Link
          to="/admin/workspaces"
          className="text-muted hover:text-foreground inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          All workspaces
        </Link>
        {workspaceQuery.isLoading ? (
          <CircleNotchIcon className="text-muted h-6 w-6 animate-spin" />
        ) : !workspace ? (
          <section className="nf-set">
            <p className="text-foreground text-base">
              {apiErrorStatus(workspaceQuery.error) === 404
                ? "This workspace doesn't exist."
                : "Could not load this workspace. Please try again."}
            </p>
          </section>
        ) : (
          <>
            <header className="space-y-2">
              <p className="text-accent-text t-eyebrow">Client workspace</p>
              <h2 className="text-foreground t-title">{workspace.name}</h2>
              <p className="text-muted text-sm">
                Created {new Date(workspace.created_at).toLocaleDateString()}
              </p>
            </header>
            <Members
              workspaceId={workspace.id}
              members={workspace.members}
              total={workspace.members_total}
              users={users}
            />
            {workspace.engagements.map((engagement) => (
              <Engagement key={engagement.id} engagement={engagement} users={users} />
            ))}
          </>
        )}
      </main>
    </div>
  );
}

interface MembersProps {
  workspaceId: string;
  members: WorkspacePerson[];
  total: number;
  users: AdminUserSummary[];
}

function Members({ workspaceId, members, total, users }: MembersProps) {
  const addMutation = useAddWorkspaceMember(workspaceId);
  const candidates = clientCandidates(users, new Set(members.map((m) => m.user_id)));
  const [message, setMessage] = useState<Message>(null);

  const add = (userId: string) => {
    setMessage(null);
    addMutation.mutate(
      { userId },
      {
        onSuccess: () => setMessage({ ok: true, text: "Client added to this workspace." }),
        onError: (err) => {
          logger.debug("[admin] add workspace member failed:", err);
          setMessage({ ok: false, text: workspaceErrorMessage(err) });
        },
      },
    );
  };

  return (
    <section className="nf-set space-y-5">
      <div className="space-y-1">
        <h3 className="text-foreground text-xl">Client users ({total})</h3>
        <p className="text-muted text-sm leading-6">
          A client user belongs to one workspace only. To invite someone new, use an
          engagement&apos;s invite page below.
        </p>
      </div>
      <PeopleList people={members} empty="No client users yet." />
      <Picker
        id={`add-client-${workspaceId}`}
        label="Add an existing client user"
        placeholder="Choose a client user"
        action="Add client"
        options={candidates}
        pending={addMutation.isPending}
        onPick={add}
      />
      <Feedback message={message} />
    </section>
  );
}

function Engagement({
  engagement,
  users,
}: {
  engagement: WorkspaceEngagement;
  users: AdminUserSummary[];
}) {
  const assignMutation = useAssignStaff(engagement.id);
  const unassignMutation = useUnassignStaff(engagement.id);
  const candidates = staffCandidates(users, new Set(engagement.assignments.map((a) => a.user_id)));
  const [message, setMessage] = useState<Message>(null);

  const onError = (err: unknown) => {
    logger.debug("[admin] staff assignment failed:", err);
    setMessage({ ok: false, text: workspaceErrorMessage(err) });
  };
  const assign = (userId: string) => {
    setMessage(null);
    assignMutation.mutate(
      { userId },
      { onSuccess: () => setMessage({ ok: true, text: "Staff member assigned." }), onError },
    );
  };
  const unassign = (person: WorkspacePerson) => {
    setMessage(null);
    unassignMutation.mutate(
      { userId: person.user_id },
      {
        onSuccess: () => setMessage({ ok: true, text: `${person.email} is no longer assigned.` }),
        onError,
      },
    );
  };

  return (
    <section className="nf-set space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-muted t-eyebrow">
            Engagement{engagement.closed_at ? " · closed" : ""}
          </p>
          <h3 className="text-foreground text-xl">{engagement.name}</h3>
        </div>
        <Link
          to={`/cockpit/engagements/${engagement.id}`}
          className="text-accent-text inline-flex items-center gap-2 text-sm hover:underline"
        >
          Invite clients
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-3">
        <h4 className="text-muted t-eyebrow">Assigned staff ({engagement.assignments_total})</h4>
        <PeopleList
          people={engagement.assignments}
          empty="No staff assigned yet."
          action={{ label: "Unassign", pending: unassignMutation.isPending, onClick: unassign }}
        />
      </div>
      <Picker
        id={`assign-staff-${engagement.id}`}
        label="Assign staff"
        placeholder="Choose a staff member"
        action="Assign staff"
        hint="BOI staff appear here after their first Google sign-in."
        options={candidates}
        pending={assignMutation.isPending}
        onPick={assign}
      />
      <Feedback message={message} />
    </section>
  );
}

interface PeopleListProps {
  people: WorkspacePerson[];
  empty: string;
  action?: { label: string; pending: boolean; onClick: (person: WorkspacePerson) => void };
}

function PeopleList({ people, empty, action }: PeopleListProps) {
  if (people.length === 0) {
    return <p className="text-muted text-sm">{empty}</p>;
  }
  return (
    <ul className="divide-border border-border divide-y rounded-lg border">
      {people.map((person) => (
        <li key={person.user_id} className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-foreground min-w-0 truncate text-sm">{personLabel(person)}</span>
          {action && (
            <Button
              variant="secondary"
              size="sm"
              disabled={action.pending}
              onClick={() => action.onClick(person)}
              aria-label={`${action.label} ${person.email}`}
            >
              {action.label}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

interface PickerProps {
  id: string;
  label: string;
  placeholder: string;
  action: string;
  hint?: string;
  options: AdminUserSummary[];
  pending: boolean;
  onPick: (userId: string) => void;
}

function Picker({ id, label, placeholder, action, hint, options, pending, onPick }: PickerProps) {
  const [userId, setUserId] = useState("");
  // A picked user who just joined drops out of the options; read the choice as cleared then.
  const selected = options.some((o) => o.id === userId) ? userId : "";

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-foreground t-eyebrow">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          id={id}
          value={selected}
          onChange={(e) => setUserId(e.target.value)}
          className="border-input bg-card text-foreground focus-visible:border-ring h-12 w-full rounded-sm border px-3.5 text-base sm:flex-1"
        >
          <option value="">{options.length ? placeholder : "Nobody left to choose"}</option>
          {options.map((user) => (
            <option key={user.id} value={user.id}>
              {personLabel(user)}
            </option>
          ))}
        </select>
        <Button disabled={!selected || pending} onClick={() => onPick(selected)}>
          {pending ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : null}
          {action}
        </Button>
      </div>
      {hint && <p className="text-muted text-xs">{hint}</p>}
    </div>
  );
}

function Feedback({ message }: { message: Message }) {
  if (!message) {
    return null;
  }
  return (
    <p
      role={message.ok ? "status" : "alert"}
      className={message.ok ? "text-success text-sm" : "text-destructive text-sm"}
    >
      {message.text}
    </p>
  );
}
